import { useMemo, useState, useRef, useLayoutEffect, useEffect } from "react";
import { Markdown } from "./Markdown";
import { activityIcon } from "./activityIcons";
import { IterationControls } from "./IterationControls";
import {
  Check,
  Loader2,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Upload,
  Search,
  FileCode,
  Play,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

// Same gradient pool as AI chat bubbles (purple)
const BUBBLE_GRADIENTS = [
  "linear-gradient(135deg, rgba(125,92,240,0.09) 0%, rgba(110,78,220,0.04) 100%)",
  "linear-gradient(150deg, rgba(130,95,245,0.08) 0%, rgba(115,82,228,0.04) 100%)",
  "linear-gradient(120deg, rgba(120,88,235,0.09) 0%, rgba(128,95,240,0.04) 100%)",
  "linear-gradient(140deg, rgba(128,94,242,0.08) 0%, rgba(105,75,218,0.04) 100%)",
  "linear-gradient(160deg, rgba(160,105,255,0.14) 0%, rgba(125,75,235,0.09) 100%)",
];

/** Max height (px) for a step's text before it gets a "Show more" toggle. */
const TEXT_COLLAPSE_HEIGHT = 72; // ~3 lines

/** Higher threshold for the final summary — keep it visible. */
const SUMMARY_COLLAPSE_HEIGHT = 280; // ~12-14 lines

/** Activities matching these patterns are considered "minor" (exploration/read-only). */
const MINOR_PATTERNS = [
  /^Reading\b/,
  /^Searching\b/,
  /^Listing\b/,
  /^Checking git\b/,
  /^Reviewing diff\b/,
];

function isMinorActivity(activity: string): boolean {
  return MINOR_PATTERNS.some((re) => re.test(activity));
}

function pickGradient(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
  return BUBBLE_GRADIENTS[Math.abs(hash) % BUBBLE_GRADIENTS.length]!;
}

/** Clean up common formatting issues in step text. */
function cleanStepText(text: string): string {
  let t = text;
  // Add space after colon when immediately followed by a capital letter
  t = t.replace(/:([A-Z])/g, ": $1");
  // Replace trailing colon with period (AI often ends thinking text with ":")
  t = t.replace(/:\s*$/, ".");
  return t;
}

export interface WorkStep {
  /** Assistant text for this step (may be empty for activity-only steps). */
  text?: string;
  /** Activity descriptions that follow this step's text. */
  activities: string[];
  /** Whether this step's text is still streaming. */
  streaming?: boolean;
  /** Message ID for keying. */
  msgId?: string;
}

/** Diff file structure matching backend's _parse_unified_diff output. */
interface DiffLine {
  type: "add" | "delete" | "context";
  content: string;
}
interface DiffHunk {
  header: string;
  lines: DiffLine[];
}
interface DiffFile {
  path: string;
  status: string;
  additions: number;
  deletions: number;
  hunks: DiffHunk[];
  binary?: boolean;
}

interface WorkProgressCardProps {
  steps: WorkStep[];
  /** True when the overall task is still in progress. */
  active: boolean;
  /** "complete" = finished normally, "budget" = hit iteration/token limit. */
  status?: "complete" | "budget";
  /** Whether the user can manually continue from this budget hit. */
  canContinue?: boolean;
  /** Callback when the Continue button is clicked. */
  onContinue?: () => void;
  /** Branch name if code was pushed during this turn. */
  pushedBranch?: string;
  /** Parsed diff files for the changes viewer. */
  diffFiles?: unknown[];
  /** Classifier task type (code_change, code_read, git_op, conversation). */
  taskType?: string;
  /** Iteration data for single-parameter tweaking. */
  iterationData?: import("./IterationControls").IterationData;
  /** Callback when user applies an iterated value. */
  onIterationApply?: (newValue: string, updatedData: import("./IterationControls").IterationData) => void;
  /** Callback when user pushes after iterating. */
  onIterationPush?: () => void;
  /** Whether an iteration apply is in progress. */
  iterationApplying?: boolean;
  /** Whether an iteration push is in progress. */
  iterationPushing?: boolean;
  /** Whether to show the push button (after successful apply). */
  iterationShowPush?: boolean;
}

/* ── Collapsible text ─────────────────────────────────── */

function CollapsibleText({
  text,
  streaming,
  collapseHeight = TEXT_COLLAPSE_HEIGHT,
  defaultExpanded = false,
}: {
  text: string;
  streaming?: boolean;
  collapseHeight?: number;
  defaultExpanded?: boolean;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = useState(false);
  const [expanded, setExpanded] = useState(defaultExpanded);

  useLayoutEffect(() => {
    if (!contentRef.current) return;
    setOverflows(contentRef.current.scrollHeight > collapseHeight + 8);
  }, [text, collapseHeight]);

  const shouldCollapse = overflows && !streaming && !expanded;

  return (
    <div>
      <div
        ref={contentRef}
        className="text-sm text-daydra-text/[0.85] leading-relaxed overflow-hidden transition-[max-height] duration-300 ease-in-out"
        style={{ maxHeight: shouldCollapse ? `${collapseHeight}px` : contentRef.current ? `${contentRef.current.scrollHeight}px` : "none" }}
      >
        <Markdown content={cleanStepText(text)} />
        {streaming && (
          <span className="inline-block w-[2px] h-[1.1em] bg-daydra-accent/60 rounded-full ml-0.5 align-middle animate-blink" />
        )}
      </div>
      {/* Chevron button below the text */}
      {overflows && !streaming && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex justify-center w-full pt-2 pb-0.5"
        >
          <ChevronDown
            size={16}
            className={`text-daydra-muted/40 hover:text-daydra-muted/70 transition-all duration-200 ${expanded ? "rotate-180" : ""}`}
          />
        </button>
      )}
    </div>
  );
}

/* ── Exploration steps ────────────────────────────────── */

/** Cycling exploration step display — shows current step, auto-advances. */
function ExplorationSteps({ steps, active, showChecks = true }: { steps: WorkStep[]; active: boolean; showChecks?: boolean }) {
  const [open, setOpen] = useState(false);
  const [visibleIdx, setVisibleIdx] = useState(0);
  const allActivities = useMemo(() => steps.flatMap((s) => s.activities), [steps]);
  const totalActivities = allActivities.length;

  // Cycle through activities when active
  useEffect(() => {
    if (!active || totalActivities <= 1) return;
    const timer = setInterval(() => {
      setVisibleIdx((prev) => Math.min(prev + 1, totalActivities - 1));
    }, 1200);
    return () => clearInterval(timer);
  }, [active, totalActivities]);

  // Keep visibleIdx at latest when new activities arrive
  useEffect(() => {
    if (active) setVisibleIdx(totalActivities - 1);
  }, [totalActivities, active]);

  const currentActivity = allActivities[visibleIdx] ?? allActivities[allActivities.length - 1];
  const CurrentIcon = currentActivity ? activityIcon(currentActivity) : Search;

  return (
    <div className="px-4 py-2 border-t border-white/[0.04]">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2.5 py-0.5 cursor-pointer"
      >
        {showChecks && (
          <div className="flex items-center justify-center w-4 h-4 shrink-0">
            {active ? (
              <Loader2 size={12} className="text-daydra-accent animate-spin" />
            ) : (
              <Check size={12} className="text-daydra-success/60" />
            )}
          </div>
        )}
        <Search size={11} className="shrink-0 text-daydra-muted/40" />
        <span className="text-xs font-medium text-daydra-muted/60 shrink-0">
          Exploration
        </span>
        <span className="text-2xs text-daydra-muted/30 shrink-0">
          {totalActivities} step{totalActivities !== 1 ? "s" : ""}
        </span>
        <ChevronDown
          size={11}
          className={`shrink-0 text-daydra-muted/30 transition-transform duration-200 ml-auto ${open ? "rotate-0" : "-rotate-90"}`}
        />
      </button>

      <div
        className="grid transition-[grid-template-rows] duration-300 ease-in-out"
        style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
      >
        <div className="overflow-hidden">
        <div className="mt-1.5 ml-3 border-l border-white/[0.06] max-h-[240px] overflow-y-auto">
          {steps.map((step, si) => (
            <div key={si}>
              {step.text && (
                <div className="pl-3 py-1 text-xs text-daydra-muted/50 italic truncate">
                  {cleanStepText(step.text).slice(0, 120)}
                </div>
              )}
              {step.activities.map((activity, ai) => {
                const Icon = activityIcon(activity);
                return (
                  <div key={ai} className="flex items-center gap-2 py-0.5 pl-3">
                    <Icon size={10} className="text-daydra-muted/30 shrink-0" />
                    <span className="text-xs text-daydra-muted/50 truncate">{activity}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        </div>
      </div>
    </div>
  );
}

/* ── Significant step ─────────────────────────────────── */

/** A significant step: shows tools prominently, thinking text in a dropdown. */
function SignificantStep({
  step,
  stepActive,
  isFinalSummary,
  isFirst,
  showChecks = true,
  expandText = false,
}: {
  step: WorkStep;
  stepActive: boolean;
  isFinalSummary: boolean;
  isFirst: boolean;
  showChecks?: boolean;
  expandText?: boolean;
}) {
  const hasText = !!step.text;
  const hasActivities = step.activities.length > 0;

  // Final summary: show text prominently with accent tint that bleeds to card edges
  if (isFinalSummary) {
    return (
      <div
        className={`px-4 py-3 ${!isFirst ? "border-t border-white/[0.06]" : ""}`}
        style={{ background: "linear-gradient(180deg, rgba(232,164,74,0.03) 0%, rgba(232,164,74,0.06) 100%)" }}
      >
        {hasText && (
          <CollapsibleText
            text={step.text!}
            streaming={step.streaming}
            collapseHeight={SUMMARY_COLLAPSE_HEIGHT}
            defaultExpanded={false}
          />
        )}
      </div>
    );
  }

  // Steps with activities: each activity is a labeled row, text expands inline
  if (hasActivities) {
    return (
      <div className={`px-4 py-2 ${!isFirst ? "border-t border-white/[0.04]" : ""}`}>
        <ActivityRows
          activities={step.activities}
          text={hasText ? step.text! : undefined}
          active={stepActive}
          showChecks={showChecks}
          expandText={expandText}
        />
      </div>
    );
  }

  // Steps with only text (streaming mid-step)
  return (
    <div className={`px-4 py-2.5 ${!isFirst ? "border-t border-white/[0.04]" : ""}`}>
      <CollapsibleText
        text={step.text!}
        streaming={step.streaming}
        collapseHeight={TEXT_COLLAPSE_HEIGHT}
      />
    </div>
  );
}

/* ── Activity rows (matching exploration dropdown style) ── */

/** Row that truncates by default but expands on click to show full text. */
function ExpandableRow({ activities, active, showChecks, PrimaryIcon }: {
  activities: string[];
  active: boolean;
  showChecks: boolean;
  PrimaryIcon: React.ComponentType<{ size: number; className?: string }>;
}) {
  const [expanded, setExpanded] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);
  const [overflows, setOverflows] = useState(false);
  // When active, show the latest activity (the one currently being worked on).
  // When done, show the collapsed summary.
  const label = active
    ? activities[activities.length - 1]
    : activities.length === 1
      ? activities[0]
      : `${activities[0]} + ${activities.length - 1} more`;

  useLayoutEffect(() => {
    const el = textRef.current;
    if (el) setOverflows(el.scrollWidth > el.clientWidth);
  }, [label]);

  const Tag = overflows ? "button" as const : "div" as const;

  return (
    <Tag
      {...(overflows ? { onClick: () => setExpanded(!expanded) } : {})}
      className={cn("w-full flex items-start gap-2.5 py-0.5", overflows && "cursor-pointer")}
    >
      {showChecks && (
        <div className="flex items-center justify-center w-4 h-4 shrink-0 mt-px">
          {active ? (
            <Loader2 size={12} className="text-daydra-accent animate-spin" />
          ) : (
            <Check size={12} className="text-daydra-success/60" />
          )}
        </div>
      )}
      <PrimaryIcon size={11} className="shrink-0 text-daydra-muted/50 mt-0.5" />
      <span
        ref={textRef}
        className={cn(
          "text-xs text-daydra-muted/70 text-left flex-1 min-w-0",
          !expanded && "truncate",
        )}
      >
        {label}
      </span>
      {overflows && (
        <ChevronDown
          size={11}
          className={`shrink-0 text-daydra-muted/30 transition-transform duration-200 mt-0.5 ${expanded ? "rotate-0" : "-rotate-90"}`}
        />
      )}
    </Tag>
  );
}

function ActivityRows({
  activities,
  text,
  active,
  showChecks = true,
  expandText = false,
}: {
  activities: string[];
  text?: string;
  active: boolean;
  showChecks?: boolean;
  expandText?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const latestActivity = activities[activities.length - 1]!;
  const PrimaryIcon = activityIcon(active ? latestActivity : activities[0]!);
  const showDropdown = !!text && !expandText;

  // When done and no text: list each activity individually instead of
  // collapsing to "Writing foo + 5 more" — users want to see every file.
  const listIndividually = !active && !text && activities.length > 1;

  return (
    <div>
      {listIndividually ? (
        <div className="flex flex-col gap-0.5">
          {activities.map((activity, i) => {
            const Icon = activityIcon(activity);
            return (
              <div key={i} className="flex items-center gap-2.5 py-0.5">
                {showChecks && (
                  <div className="flex items-center justify-center w-4 h-4 shrink-0">
                    <Check size={12} className="text-daydra-success/60" />
                  </div>
                )}
                <Icon size={11} className="shrink-0 text-daydra-muted/50" />
                <span className="text-xs text-daydra-muted/70 truncate">{activity}</span>
              </div>
            );
          })}
        </div>
      ) : showDropdown ? (
        <button
          onClick={() => setOpen(!open)}
          className="w-full flex items-center gap-2.5 py-0.5 cursor-pointer"
        >
          {showChecks && (
            <div className="flex items-center justify-center w-4 h-4 shrink-0">
              {active ? (
                <Loader2 size={12} className="text-daydra-accent animate-spin" />
              ) : (
                <Check size={12} className="text-daydra-success/60" />
              )}
            </div>
          )}
          <PrimaryIcon size={11} className="shrink-0 text-daydra-muted/50" />
          <span className="text-xs text-daydra-muted/70 group-hover:text-daydra-dim transition-colors truncate text-left flex-1 min-w-0">
            {active
              ? latestActivity
              : activities.length === 1
                ? activities[0]
                : `${activities[0]} + ${activities.length - 1} more`}
          </span>
          <ChevronDown
            size={11}
            className={`shrink-0 text-daydra-muted/30 transition-transform duration-200 ${open ? "rotate-0" : "-rotate-90"}`}
          />
        </button>
      ) : (
        <ExpandableRow activities={activities} active={active} showChecks={showChecks} PrimaryIcon={PrimaryIcon} />
      )}

      {text && expandText && (
        <div className="mt-1.5 ml-3 border-l border-white/[0.06] pl-3 text-sm text-daydra-text/[0.85] leading-relaxed">
          <Markdown content={cleanStepText(text)} />
        </div>
      )}
      {text && !expandText && (
        <div
          className="grid transition-[grid-template-rows] duration-300 ease-in-out"
          style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
        >
          <div className="overflow-hidden">
            <div className="mt-1.5 ml-3 border-l border-white/[0.06] pl-3">
              {activities.length > 1 && (
                <div className="flex flex-col gap-0.5 mb-2">
                  {activities.map((activity, i) => {
                    const Icon = activityIcon(activity);
                    return (
                      <div key={i} className="flex items-center gap-2 py-0.5">
                        <Icon size={11} className="shrink-0 text-daydra-muted/50" />
                        <span className="text-xs text-daydra-muted/70 truncate">{activity}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="text-sm text-daydra-text/[0.85] leading-relaxed">
                <Markdown content={cleanStepText(text)} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Inline diff viewer ───────────────────────────────── */

function DiffFileContent({ file }: { file: DiffFile }) {
  return file.binary ? (
    <div className="px-4 py-2 text-xs text-daydra-muted/40 italic">Binary file</div>
  ) : (
    <div className="overflow-x-auto bg-black/20">
      {file.hunks.map((hunk, hi) => (
        <div key={hi} className="font-mono text-xs leading-[1.6]">
          {hunk.lines.map((line, li) => (
            <div
              key={li}
              className={cn(
                "flex min-w-fit px-4",
                line.type === "add" && "bg-green-500/8",
                line.type === "delete" && "bg-red-500/8",
              )}
            >
              <span
                className={cn(
                  "w-4 shrink-0 text-center select-none",
                  line.type === "add" && "text-green-500/50",
                  line.type === "delete" && "text-red-500/50",
                  line.type === "context" && "text-daydra-faint",
                )}
              >
                {line.type === "add" ? "+" : line.type === "delete" ? "−" : " "}
              </span>
              <pre
                className={cn(
                  "flex-1 pr-3 whitespace-pre",
                  line.type === "add" && "text-green-400/80",
                  line.type === "delete" && "text-red-400/80",
                  line.type === "context" && "text-daydra-dim/60",
                )}
              >
                {line.content || "\n"}
              </pre>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function InlineDiffViewer({ files }: { files: DiffFile[] }) {
  const [expandedFiles, setExpandedFiles] = useState<Set<number>>(new Set());

  const toggleFile = (idx: number) => {
    setExpandedFiles((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  // Single file: show inline, no dropdown
  if (files.length === 1) {
    const file = files[0]!;
    return (
      <div className="max-h-[400px] overflow-y-auto">
        <div className="flex items-center gap-2 px-4 py-1.5">
          <span className="font-mono text-xs text-daydra-dim/80 truncate flex-1 min-w-0">{file.path}</span>
          <span className="text-2xs text-green-400/60 shrink-0">+{file.additions}</span>
          <span className="text-2xs text-red-400/60 shrink-0">-{file.deletions}</span>
        </div>
        <DiffFileContent file={file} />
      </div>
    );
  }

  // Multiple files: each under a dropdown
  return (
    <div className="max-h-[400px] overflow-y-auto">
      {files.map((file, fi) => {
        const isExpanded = expandedFiles.has(fi);
        return (
          <div key={fi} className={fi > 0 ? "border-t border-white/[0.04]" : ""}>
            <button
              onClick={() => toggleFile(fi)}
              className="w-full flex items-center gap-2 px-4 py-1.5 text-left hover:bg-white/[0.02] transition-colors"
            >
              {isExpanded ? (
                <ChevronDown size={10} className="text-daydra-muted/40 shrink-0" />
              ) : (
                <ChevronRight size={10} className="text-daydra-muted/40 shrink-0" />
              )}
              <span className="font-mono text-xs text-daydra-dim/80 truncate flex-1 min-w-0">
                {file.path}
              </span>
              <span className="text-2xs text-green-400/60 shrink-0">+{file.additions}</span>
              <span className="text-2xs text-red-400/60 shrink-0">-{file.deletions}</span>
            </button>
            <div
              className="grid transition-[grid-template-rows] duration-300 ease-in-out"
              style={{ gridTemplateRows: isExpanded ? "1fr" : "0fr" }}
            >
              <div className="overflow-hidden">
                <DiffFileContent file={file} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Step grouping logic ──────────────────────────────── */

type StepGroup =
  | { kind: "significant"; step: WorkStep; index: number }
  | { kind: "minor"; steps: WorkStep[] }
  | { kind: "separator" };

function groupSteps(steps: WorkStep[]): StepGroup[] {
  const groups: StepGroup[] = [];
  let minorBuffer: WorkStep[] = [];

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i]!;
    const isLast = i === steps.length - 1;

    // Continuation separator marker
    if (step.activities.length === 1 && step.activities[0] === "__continuation__") {
      if (minorBuffer.length > 0) {
        groups.push({ kind: "minor", steps: minorBuffer });
        minorBuffer = [];
      }
      groups.push({ kind: "separator" });
      continue;
    }

    const hasSignificantActivity = step.activities.some((a) => !isMinorActivity(a));
    const isPureText = !!step.text && step.activities.length === 0;
    const isSignificant = isLast || isPureText || hasSignificantActivity;

    if (isSignificant) {
      if (minorBuffer.length > 0) {
        groups.push({ kind: "minor", steps: minorBuffer });
        minorBuffer = [];
      }
      groups.push({ kind: "significant", step, index: i });
    } else {
      minorBuffer.push(step);
    }
  }

  if (minorBuffer.length > 0) {
    groups.push({ kind: "minor", steps: minorBuffer });
  }

  return groups;
}

/* ── Task type labels ────────────────────────────────── */

const TASK_TYPE_LABELS: Record<string, string> = {
  code_change: "Code",
  code_read: "Read",
  git_op: "Git",
  conversation: "Chat",
  setup_scaffolding: "Project created",
  setup_gathering: "Setup",
};

/* ── Tab types ──────────────────────────────────────── */

type TabId = "summary" | "activity" | "changes" | "adjust";
interface TabDef { id: TabId; label: string }

/* ── TabBar ─────────────────────────────────────────── */

const TAB_TINT = "rgba(255,255,255,0.04)";

function TabBar({ tabs, activeTab, onTabChange }: {
  tabs: TabDef[];
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
}) {
  const activeIdx = tabs.findIndex(t => t.id === activeTab);

  return (
    <div className="flex items-stretch">
      {tabs.map((tab, i) => {
        const isActive = tab.id === activeTab;
        const isFirst = i === 0;
        const isLast = i === tabs.length - 1;
        return (
          <div key={tab.id} className="relative flex-1 flex flex-col items-stretch">
            <button
              onClick={() => onTabChange(tab.id)}
            className={cn(
              "flex-1 py-4 text-sm font-medium cursor-pointer text-center min-w-0",
              isFirst ? "pl-6 pr-3" : isLast ? "pl-3 pr-6" : "px-3",
              isActive
                ? "text-daydra-text"
                : "text-daydra-muted/40 hover:text-daydra-muted/70",
            )}
            style={{
              background: isActive ? "transparent" : TAB_TINT,
              boxShadow: isActive
                ? "none"
                : "inset 0 4px 6px -2px rgba(0,0,0,0.3), inset 0 -4px 8px -4px rgba(0,0,0,0.25)",
              transition: "background 200ms, color 200ms, box-shadow 200ms",
            }}
          >
            {tab.label}
          </button>
          {isActive && <div className="h-px mx-4 bg-white/[0.08]" />}
          </div>
        );
      })}
    </div>
  );
}

/* ── Animated tab content area ──────────────────────── */

function TabContentArea({ activeTab, children }: {
  activeTab: string;
  children: React.ReactNode;
}) {
  return (
    <AnimatePresence mode="popLayout" initial={false}>
      <motion.div
        key={activeTab}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

/* ── Main card ────────────────────────────────────────── */

export function WorkProgressCard({ steps, active, status, canContinue, onContinue, pushedBranch, diffFiles, taskType, iterationData, onIterationApply, onIterationPush, iterationApplying, iterationPushing, iterationShowPush }: WorkProgressCardProps) {
  const totalActivities = steps.reduce(
    (n, s) => n + s.activities.filter((a) => a !== "__continuation__").length, 0,
  );

  const gradient = useMemo(
    () => pickGradient(steps[0]?.text ?? steps[0]?.msgId ?? "work"),
    [steps[0]?.text, steps[0]?.msgId],
  );

  const hitBudget = status === "budget";
  const stepGroups = useMemo(() => groupSteps(steps), [steps]);
  const parsedDiffFiles = diffFiles as DiffFile[] | undefined;
  const hasDiffs = parsedDiffFiles && parsedDiffFiles.length > 0;

  // When not active, separate the final summary from the rest of the steps
  const { regularGroups, summaryStep } = useMemo(() => {
    if (active) return { regularGroups: stepGroups, summaryStep: null as WorkStep | null };

    const lastGroup = stepGroups[stepGroups.length - 1];
    if (lastGroup?.kind === "significant") {
      const { step, index } = lastGroup;
      if (index === steps.length - 1 && !!step.text && step.activities.length === 0) {
        return { regularGroups: stepGroups.slice(0, -1), summaryStep: step };
      }
    }
    return { regularGroups: stepGroups, summaryStep: null as WorkStep | null };
  }, [stepGroups, active, steps.length]);

  // Tab state
  const [activeTab, setActiveTab] = useState<TabId>("summary");
  const [tabsRevealed, setTabsRevealed] = useState(!active);
  const hasBeenActive = useRef(false);
  useEffect(() => { if (active) hasBeenActive.current = true; }, [active]);

  // Transition: active → done/paused (delay tab reveal, default to summary)
  useEffect(() => {
    if (active) {
      setTabsRevealed(false);
    } else {
      setActiveTab("summary");
      const timer = setTimeout(() => setTabsRevealed(true), 600);
      return () => clearTimeout(timer);
    }
  }, [active]);

  // Available tabs (only include tabs with content)
  const availableTabs = useMemo<TabDef[]>(() => {
    const tabs: TabDef[] = [];
    if (summaryStep) tabs.push({ id: "summary", label: "Summary" });
    if (regularGroups.length > 0) tabs.push({ id: "activity", label: "Activity" });
    if (hasDiffs || pushedBranch) tabs.push({ id: "changes", label: "Changes" });
    if (iterationData && onIterationApply) tabs.push({ id: "adjust", label: "Adjust" });
    return tabs;
  }, [summaryStep, regularGroups.length, hasDiffs, pushedBranch, active, iterationData, onIterationApply]);

  // Keep activeTab valid when available tabs change. Only fall back
  // when the current tab disappears — never yank the user off a tab
  // they picked just because the memo re-computed.
  useEffect(() => {
    if (availableTabs.length === 0) return;
    if (!availableTabs.some(t => t.id === activeTab)) {
      setActiveTab(availableTabs[0]!.id);
    }
  }, [availableTabs, activeTab]);

  const showTabBar = !active && tabsRevealed && availableTabs.length > 1;
  const showHeader = active || !tabsRevealed || availableTabs.length <= 1;
  const effectiveTab = availableTabs.length === 1 ? availableTabs[0]?.id ?? "summary" : activeTab;

  // Render step groups (shared between active state and activity tab)
  const renderStepGroups = (groups: StepGroup[], isActive: boolean, showChecks = true, expandText = false) =>
    groups.map((group, gi) => {
      if (group.kind === "separator") {
        return (
          <div key={`sep-${gi}`} className="flex items-center gap-3 px-4 py-2">
            <div className="flex-1 border-t border-white/[0.08]" />
            <span className="text-2xs text-daydra-muted shrink-0">Continuing</span>
            <div className="flex-1 border-t border-white/[0.08]" />
          </div>
        );
      }
      if (group.kind === "minor") {
        const isLastGroup = gi === groups.length - 1;
        return (
          <ExplorationSteps
            key={`minor-${gi}`}
            steps={group.steps}
            active={isActive && isLastGroup}
            showChecks={showChecks}
          />
        );
      }
      const { step, index } = group;
      const isLast = index === steps.length - 1;
      const stepActive = isActive && isLast;
      const isFinalSummary = hitBudget && isLast && !isActive && !!step.text && step.activities.length === 0;
      return (
        <SignificantStep
          key={index}
          step={step}
          stepActive={stepActive}
          isFinalSummary={isFinalSummary}
          isFirst={gi === 0}
          showChecks={showChecks}
          expandText={expandText}
        />
      );
    });

  return (
    <div
      className="overflow-hidden max-w-2xl rounded-3xl rounded-bl-lg"
      style={{ background: gradient }}
    >
      {/* Header — only shown during active state and single-tab done state */}
      {showHeader && (
        <div className="flex items-center gap-2 px-4 py-3">
          <div className="flex items-baseline gap-2 flex-1 min-w-0">
            {active ? (
              <>
                <Loader2 size={14} className="text-daydra-accent animate-spin self-center shrink-0" />
                <span className="text-sm font-semibold text-daydra-accent">Working</span>
              </>
            ) : hitBudget ? (
              <>
                <AlertCircle size={14} className="text-daydra-warning self-center shrink-0" />
                <span className="text-sm font-semibold text-daydra-warning">Paused</span>
              </>
            ) : (
              <span className="text-sm font-semibold text-daydra-success">
                {(taskType && TASK_TYPE_LABELS[taskType]) || "Done"}
              </span>
            )}
            <span className="text-2xs text-daydra-muted">
              {totalActivities} action{totalActivities !== 1 ? "s" : ""}
              {active ? " so far" : hitBudget ? " — hit limit" : ""}
            </span>
          </div>

          {/* Push badge — inline indicator when active */}
          {pushedBranch && active && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-daydra-success/[0.12] border border-daydra-success/20 shrink-0">
              <Upload size={10} className="text-daydra-success" />
              <span className="text-2xs font-medium text-daydra-success whitespace-nowrap">
                pushed to {pushedBranch}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Steps area — visible when active, collapses when tabs appear */}
      <div
        className="grid transition-[grid-template-rows] duration-500 ease-in-out"
        style={{ gridTemplateRows: (!active && tabsRevealed) ? "0fr" : "1fr" }}
      >
        <div className="overflow-hidden">
          <div className="border-t border-white/[0.06]">
            {renderStepGroups(tabsRevealed ? regularGroups : stepGroups, active)}
          </div>
        </div>
      </div>

      {/* Tab bar — slides in when done/paused with multiple tabs */}
      {showTabBar && (
        <motion.div
          initial={hasBeenActive.current ? { opacity: 0, height: 0 } : false}
          animate={{ opacity: 1, height: "auto" }}
          transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="overflow-hidden"
        >
          <TabBar
            tabs={availableTabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
        </motion.div>
      )}

      {/* Tab content — shown when tabs are revealed */}
      {!active && tabsRevealed && availableTabs.length > 0 && (
        <motion.div
          initial={hasBeenActive.current ? { opacity: 0 } : false}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <TabContentArea activeTab={effectiveTab}>
            {effectiveTab === "summary" && summaryStep && (
              <div className="px-4 py-3">
                <CollapsibleText
                  text={summaryStep.text!}
                  collapseHeight={SUMMARY_COLLAPSE_HEIGHT}
                  defaultExpanded={false}
                />
              </div>
            )}
            {effectiveTab === "activity" && (
              <div>
                {renderStepGroups(regularGroups, false, false, true)}
              </div>
            )}
            {effectiveTab === "changes" && (
              <div>
                {pushedBranch && (
                  <div
                    className="flex items-center gap-2.5 px-4 py-2.5"
                    style={{ background: "linear-gradient(180deg, rgba(74,232,128,0.03) 0%, rgba(74,232,128,0.06) 100%)" }}
                  >
                    <div className="flex items-center justify-center w-4 h-4 shrink-0">
                      <Check size={12} className="text-daydra-success" />
                    </div>
                    <Upload size={12} className="text-daydra-success shrink-0" />
                    <span className="text-xs font-medium text-daydra-success">
                      Pushed to {pushedBranch}
                    </span>
                  </div>
                )}
                {hasDiffs && <InlineDiffViewer files={parsedDiffFiles} />}
              </div>
            )}
            {effectiveTab === "adjust" && iterationData && onIterationApply && (
              <div className="px-4 py-3">
                <IterationControls
                  data={iterationData}
                  onApply={onIterationApply}
                  onPush={onIterationPush}
                  showPush={iterationShowPush}
                  applying={iterationApplying}
                  pushing={iterationPushing}
                  compact
                />
              </div>
            )}
          </TabContentArea>
        </motion.div>
      )}

      {/* Budget limit — below tabs, always visible when applicable */}
      {hitBudget && !active && (
        canContinue && onContinue ? (
          <button
            onClick={onContinue}
            className="flex items-center gap-2 w-full border-t border-white/[0.06] px-4 py-2.5 text-xs text-daydra-muted hover:text-daydra-accent transition-colors cursor-pointer"
          >
            <Play size={10} className="shrink-0" />
            <span>Continue where it left off</span>
          </button>
        ) : (
          <div className="border-t border-white/[0.06] px-4 py-2.5 text-xs text-daydra-muted">
            Reached operation limit. Send a follow-up to continue where it left off.
          </div>
        )
      )}

      {/* Active progress bar */}
      {active && (
        <div className="h-0.5 overflow-hidden">
          <div
            className="h-full w-1/3 rounded-full"
            style={{
              background: "var(--brand-gradient)",
              animation: "progressSlide 1.5s ease-in-out infinite",
            }}
          />
        </div>
      )}
    </div>
  );
}
